import { h, html, useState, useEffect, useCallback } from '../setup.js';
import { logger } from '../../../core/logger.js';

// Helper-Funktion für benutzerfreundliche Fehlermeldungen
function getAuthErrorMessage(errorCode, t, additionalData = {}) {
  const errorMessages = {
    'auth.error.username_short': t('auth.error.username_short', 'Username muss zwischen 3 und 25 Zeichen lang sein.'),
    'auth.error.username_invalid_chars': t('auth.error.username_invalid_chars', 'Username darf nur Buchstaben, Zahlen und Unterstriche enthalten.'),
    'auth.error.username_blacklisted': t('auth.error.username_blacklisted', 'Dieser Username ist nicht erlaubt.'),
    'auth.error.username_taken': t('auth.error.username_taken', 'Dieser Username ist bereits vergeben.'),
    'auth.error.username_too_similar': t('auth.error.username_too_similar', 
      `Dieser Username ist zu ähnlich zu "${additionalData.similarTo || 'einem bestehenden User'}". Bitte wähle einen anderen.`),
    'auth.error.email_invalid': t('auth.error.email_invalid', 'Bitte gib eine gültige E-Mail-Adresse ein.'),
    'auth.error.email_taken': t('auth.error.email_taken', 'Diese E-Mail-Adresse wird bereits verwendet.'),
    'auth.error.password_short': t('auth.error.password_short', 'Passwort muss mindestens 6 Zeichen lang sein.'),
    'auth.error.password_too_long': t('auth.error.password_too_long', 'Passwort darf maximal 128 Zeichen lang sein.'),
    'auth.error.rate_limit': t('auth.error.rate_limit', 
      `Zu viele Versuche. Bitte warte ${additionalData.retryAfter || 60} Sekunden.`),
    'auth.error.wrong_password': t('auth.error.wrong_password', 'Falsches Passwort.'),
    'auth.error.user_not_found': t('auth.error.user_not_found', 'Kein Account mit diesen Daten gefunden.'),
    'auth.error.not_guest': t('auth.error.not_guest', 'Nur Gast-Accounts können umgewandelt werden.'),
    'auth.error.server_error': t('auth.error.server_error', 'Server-Fehler. Bitte versuche es später erneut.'),
    'auth.error.server_timeout': t('auth.error.server_timeout', 'Server antwortet nicht. Offline-Modus aktiv.'),
    'auth.error.missing_fields': t('auth.error.missing_fields', 'Bitte fülle alle Felder aus.')
  };
  
  return errorMessages[errorCode] || t('common.error', 'Ein unerwarteter Fehler ist aufgetreten.');
}

export function LoginView({ eventBus, services }) {
  const { authService, i18nService } = services || {};

  const t = (key, fallback = key) => i18nService ? i18nService.t(key, fallback) : fallback;

  const [currentUser, setCurrentUser] = useState(authService ? authService.getCurrentUser() : null);
  const [activeTab, setActiveTab] = useState('login'); // 'login' | 'register'

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isLoggedIn = currentUser && !currentUser.isGuest;

  useEffect(() => {
    if (eventBus) {
      const subId = eventBus.subscribe('auth:stateChanged', (data) => {
        if (data && data.user) {
          setCurrentUser(data.user);
        }
      });
      return () => eventBus.unsubscribe(subId);
    }
  }, [eventBus]);

  useEffect(() => {
    setErrorMessage('');
    setSuccessMessage('');
  }, [activeTab]);

  const handleProceedToMenu = useCallback(() => {
    if (eventBus) {
      eventBus.publish('auth:proceedToMenu');
    }
  }, [eventBus]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!authService) return;

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const res = await authService.login(username, password);
      if (res.success) {
        setSuccessMessage(t('auth.success.login', 'Willkommen zurück!'));
        setTimeout(() => {
          handleProceedToMenu();
        }, 800);
      } else {
        const errorMsg = getAuthErrorMessage(res.error, t, {
          similarTo: res.similarTo,
          retryAfter: res.retryAfter
        });
        setErrorMessage(errorMsg);
      }
    } catch (err) {
      logger.error('[Auth] Unerwarteter Fehler:', err);
      setErrorMessage(t('common.error', 'Ein unerwarteter Fehler ist aufgetreten.'));
    } finally {
      setLoading(false);
    }
  }, [authService, username, password, handleProceedToMenu, t]);

  const handleRegister = useCallback(async (e) => {
    e.preventDefault();
    if (!authService) return;

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const res = await authService.register(username, email, password);
      if (res.success) {
        setSuccessMessage(t('auth.success.registered', 'Konto erfolgreich erstellt! Willkommen im Archiv.'));
        setTimeout(() => {
          handleProceedToMenu();
        }, 800);
      } else {
        const errorMsg = getAuthErrorMessage(res.error, t, {
          similarTo: res.similarTo,
          retryAfter: res.retryAfter
        });
        setErrorMessage(errorMsg);
      }
    } catch (err) {
      logger.error('[Auth] Unerwarteter Fehler:', err);
      setErrorMessage(t('common.error', 'Ein unerwarteter Fehler ist aufgetreten.'));
    } finally {
      setLoading(false);
    }
  }, [authService, username, email, password, handleProceedToMenu, t]);

  const handleGuestContinue = useCallback(() => {
    handleProceedToMenu();
  }, [handleProceedToMenu]);

  const handleLogout = useCallback(() => {
    if (authService) {
      authService.logout();
      setSuccessMessage('Erfolgreich abgemeldet.');
      setCurrentUser(authService.getCurrentUser());
    }
  }, [authService]);

  const handleQuit = useCallback(() => {
    if (eventBus) {
      eventBus.publish('menu:quit');
    }
  }, [eventBus]);

  const toggleLanguage = (langKey) => {
    if (eventBus) {
      eventBus.publish('options:setLanguage', { value: langKey });
    }
  };

  const currentLang = i18nService ? i18nService.getLanguage() : 'de';

  return html`
    <section id="login-container" class="center-layout fade-in" role="main" aria-label="Login-Portal" style="display: flex; flex-direction: column; align-items: center; justify-content: center; position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 100; pointer-events: auto;">
      
      <!-- PORTAL DER ERINNERUNGEN: SPEZIELLER HINTERGRUND VFX -->
      <div class="login-bg-portal" aria-hidden="true">
        <div class="login-god-rays"></div>
        <div class="login-portal-core"></div>
        <div class="login-rune-ring">
          <svg viewBox="0 0 500 500" style="width: 100%; height: 100%;">
            <!-- Äußerer Runenkreis -->
            <circle cx="250" cy="250" r="230" fill="none" stroke="rgba(197,160,89,0.2)" stroke-width="1.5" stroke-dasharray="8 6" />
            <circle cx="250" cy="250" r="215" fill="none" stroke="rgba(197,160,89,0.12)" stroke-width="1" />
            
            <!-- Innere Geometrie -->
            <polygon points="250,35 436,357 64,357" fill="none" stroke="rgba(197,160,89,0.1)" stroke-width="1" />
            <polygon points="250,465 64,143 436,143" fill="none" stroke="rgba(197,160,89,0.1)" stroke-width="1" />
            <circle cx="250" cy="250" r="160" fill="none" stroke="rgba(197,160,89,0.25)" stroke-width="1" stroke-dasharray="12 12" />

            <!-- Runen-Glyphen -->
            <text x="250" y="28" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚠ</text>
            <text x="440" y="98" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚢ</text>
            <text x="480" y="255" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚦ</text>
            <text x="430" y="415" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚨ</text>
            <text x="250" y="488" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚱ</text>
            <text x="70" y="415" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚲ</text>
            <text x="20" y="255" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚷ</text>
            <text x="60" y="98" text-anchor="middle" fill="rgba(197,160,89,0.6)" font-size="14" font-family="Cinzel, serif">ᚹ</text>
          </svg>
        </div>
      </div>

      <!-- Bottom Left Exit Button -->
      <div style="position: absolute; bottom: 24px; left: 24px; z-index: 20; pointer-events: auto;">
        <button 
          class="glass-btn btn-small" 
          onClick=${handleQuit}
          style="display: flex; align-items: center; gap: 6px; padding: 8px 14px; font-size: 0.82rem; border-color: rgba(197, 160, 89, 0.3); opacity: 0.85; transition: all var(--transition-default);"
          title=${t('menu.quit', 'Beenden')}
        >
          <span>🚪</span>
          <span class="cinzel" style="letter-spacing: 1px;">${t('menu.quit', 'Beenden')}</span>
        </button>
      </div>

      <!-- Title Header -->
      <div style="text-align: center; margin-bottom: 24px; position: relative; z-index: 10;">
        <h1 class="glow-text text-gold cinzel" style="font-size: clamp(2.2rem, 4.8vw, 3.4rem); text-transform: uppercase; margin-bottom: 6px;">
          ${t('menu.title', 'ARCHIV DES VERGESSENS')}
        </h1>
        <p class="subtitle cinzel text-gold" style="font-size: 0.95rem; letter-spacing: 5px; margin: 0; text-transform: uppercase; text-shadow: 0 0 10px var(--color-gold-glow);">
          ✦ LOGIN PORTAL ✦
        </p>
      </div>

      <!-- Main Login Card -->
      <div class="glass-panel login-card">
        
        ${isLoggedIn ? html`
          <!-- BEREITS ANGEMELDET -->
          <div style="display: flex; flex-direction: column; gap: 20px; text-align: center;">
            <div style="font-size: 3rem; background: rgba(197, 160, 89, 0.1); width: 84px; height: 80px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; border: 2px solid var(--color-gold); box-shadow: 0 0 18px var(--color-gold-glow);">
              ${currentUser.avatar || '⚔️'}
            </div>
            <div>
              <div class="cinzel text-gold" style="font-size: 1.35rem; font-weight: 700; letter-spacing: 1px;">${currentUser.username}</div>
              <div class="text-muted" style="font-size: 0.85rem; margin-top: 4px;">${currentUser.email || 'Hüter des Archivs'}</div>
            </div>

            <button class="glass-btn primary w-100" onClick=${handleProceedToMenu} style="padding: 13px; font-size: 0.95rem; font-weight: 700;">
              🚀 ${t('menu.continueToCharSelect', 'Weiter zur Charakterauswahl')} »
            </button>

            <button class="glass-btn btn-danger w-100" onClick=${handleLogout} style="padding: 10px; font-size: 0.85rem;">
              🚪 ${t('auth.logout', 'Abmelden')}
            </button>
          </div>
        ` : html`
          <!-- LOGIN / REGISTRIERUNG FORM -->
          
          <!-- Tab Navigation -->
          <div class="form-tabs">
            <button 
              class=${`form-tab-btn ${activeTab === 'login' ? 'active' : ''}`}
              onClick=${() => setActiveTab('login')}
            >
              🔑 ${t('auth.login', 'Anmelden')}
            </button>
            <button 
              class=${`form-tab-btn ${activeTab === 'register' ? 'active' : ''}`}
              onClick=${() => setActiveTab('register')}
            >
              ✨ ${t('auth.register', 'Registrieren')}
            </button>
          </div>

          <!-- Banners -->
          ${errorMessage && html`
            <div class="auth-error-box error-shake" style="
              background: rgba(255, 59, 48, 0.15);
              border: 1px solid rgba(255, 59, 48, 0.4);
              border-radius: 8px;
              padding: 12px 16px;
              margin: 12px 0;
              color: #ff6b6b;
              display: flex;
              align-items: flex-start;
              gap: 8px;
              animation: slideIn 0.3s ease-out;
            ">
              <span style="font-size: 20px; flex-shrink: 0;">⚠️</span>
              <div style="flex: 1; line-height: 1.4;">
                <div style="font-weight: 600; margin-bottom: 4px;">Fehler</div>
                <div style="font-size: 14px;">${errorMessage}</div>
              </div>
              <button 
                onClick=${() => setErrorMessage('')}
                style="
                  background: none;
                  border: none;
                  color: #ff6b6b;
                  cursor: pointer;
                  padding: 4px;
                  font-size: 18px;
                  opacity: 0.7;
                "
              >×</button>
            </div>
          `}
          
          ${successMessage && html`
            <div class="auth-success-box" style="
              background: rgba(52, 199, 89, 0.15);
              border: 1px solid rgba(52, 199, 89, 0.4);
              border-radius: 8px;
              padding: 12px 16px;
              margin: 12px 0;
              color: #52c41a;
              display: flex;
              align-items: flex-start;
              gap: 8px;
              animation: slideIn 0.3s ease-out;
            ">
              <span style="font-size: 20px; flex-shrink: 0;">✓</span>
              <div style="flex: 1; line-height: 1.4;">
                <div style="font-weight: 600; margin-bottom: 4px;">Erfolgreich</div>
                <div style="font-size: 14px;">${successMessage}</div>
              </div>
            </div>
          `}


          <!-- LOGIN FORM -->
          ${activeTab === 'login' && html`
            <form onSubmit=${handleLogin} style="display: flex; flex-direction: column; gap: 14px;">
              <div>
                <label class="cinzel text-gold" style="display: block; font-size: 0.8rem; margin-bottom: 6px; letter-spacing: 0.5px;">${t('auth.username', 'Benutzername')} / ${t('auth.email', 'E-Mail')}</label>
                <input type="text" class="form-input" required autoFocus autofocus value=${username} onInput=${(e) => setUsername(e.target.value)} placeholder="z. B. MnemeHüter" />
              </div>
              <div>
                <label class="cinzel text-gold" style="display: block; font-size: 0.8rem; margin-bottom: 6px; letter-spacing: 0.5px;">${t('auth.password', 'Passwort')}</label>
                <input type="password" class="form-input" required value=${password} onInput=${(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>

              <button type="submit" disabled=${loading || (authService && (authService.isAuthenticating || authService._isAuthenticating))} class="glass-btn primary w-100" style="margin-top: 6px; padding: 12px; display: flex; align-items: center; justify-content: center; gap: 8px;">
                ${(loading || (authService && (authService.isAuthenticating || authService._isAuthenticating))) ? html`<span class="loading-spinner" style="display: inline-block; animation: spin 1s linear infinite;">⏳</span> ${t('auth.authenticating', 'Authentifizierung...')}` : `🔑 ${t('auth.login', 'Anmelden')}`}
              </button>
            </form>
          `}

          <!-- REGISTER FORM -->
          ${activeTab === 'register' && html`
            <form onSubmit=${handleRegister} style="display: flex; flex-direction: column; gap: 14px;">
              <div>
                <label class="cinzel text-gold" style="display: block; font-size: 0.8rem; margin-bottom: 6px; letter-spacing: 0.5px;">${t('auth.username', 'Benutzername')}</label>
                <input type="text" class="form-input" required autoFocus autofocus minlength="3" value=${username} onInput=${(e) => setUsername(e.target.value)} placeholder="Dein Spielername" />
              </div>
              <div>
                <label class="cinzel text-gold" style="display: block; font-size: 0.8rem; margin-bottom: 6px; letter-spacing: 0.5px;">${t('auth.email', 'E-Mail-Adresse')}</label>
                <input type="email" class="form-input" required value=${email} onInput=${(e) => setEmail(e.target.value)} placeholder="name@beispiel.de" />
              </div>
              <div>
                <label class="cinzel text-gold" style="display: block; font-size: 0.8rem; margin-bottom: 6px; letter-spacing: 0.5px;">${t('auth.password', 'Passwort')}</label>
                <input type="password" class="form-input" required minlength="6" value=${password} onInput=${(e) => setPassword(e.target.value)} placeholder="Mindestens 6 Zeichen" />
              </div>

              <button type="submit" disabled=${loading || (authService && (authService.isAuthenticating || authService._isAuthenticating))} class="glass-btn primary w-100" style="margin-top: 6px; padding: 12px; display: flex; align-items: center; justify-content: center; gap: 8px;">
                ${(loading || (authService && (authService.isAuthenticating || authService._isAuthenticating))) ? html`<span class="loading-spinner" style="display: inline-block; animation: spin 1s linear infinite;">⏳</span> ${t('auth.registering', 'Erstelle Konto...')}` : `✨ ${t('auth.register', 'Konto erstellen')}`}
              </button>
            </form>
          `}

          <!-- DIVIDER -->
          <div class="form-divider">
            <span class="form-divider-text">✦ ODER ✦</span>
          </div>

          <!-- GUEST MODE BUTTON -->
          <button type="button" class="glass-btn w-100" onClick=${handleGuestContinue} style="border-color: rgba(197, 160, 89, 0.4); color: var(--color-gold);">
            🛡️ ${t('auth.guest', 'Als Gast fortfahren')}
          </button>
        `}
      </div>

      <!-- Footer Language Selector & Info -->
      <div style="margin-top: 24px; display: flex; align-items: center; gap: 12px; font-size: 0.8rem; position: relative; z-index: 10;">
        <button class=${`glass-btn btn-small ${currentLang === 'de' ? 'primary' : ''}`} onClick=${() => toggleLanguage('de')}>🇩🇪 Deutsch</button>
        <span class="text-gold" style="opacity: 0.4;">|</span>
        <button class=${`glass-btn btn-small ${currentLang === 'en' ? 'primary' : ''}`} onClick=${() => toggleLanguage('en')}>🇬🇧 English</button>
      </div>

    </section>
  `;
}

export default LoginView;
